import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  Grid,
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  CheckCircle,
  Cancel,
  Person,
  Phone,
  Biotech,
  Business,
  Warning
} from '@mui/icons-material';
import { orderAssignmentAPI } from '../deliveryAgentAPI';

const PhlebotomistOrderDetails = ({ order, onBack, onOrderUpdate, onReportFailure }) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });

  const isPending = order.assignmentStatus === 'pending';
  const isAccepted = order.assignmentStatus === 'accepted';
  const isSampleCollected = order.orderStatus === 'sample-collected';
  const isCompleted = order.orderStatus === 'completed' || order.orderStatus === 'lab-received';

  const handleAcceptAssignment = async () => {
    try {
      setLoading(true);
      
      const assignmentToken = order.assignmentToken;
      
      if (!assignmentToken) {
        setSnackbar({
          open: true,
          message: 'Assignment token not found. Please contact support.',
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      const response = await orderAssignmentAPI.acceptAssignment(assignmentToken);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Assignment accepted! You can now collect samples.',
          severity: 'success'
        });
        
        setTimeout(() => {
          onOrderUpdate();
        }, 1500);
      }
    } catch (error) {
      console.error('Accept assignment error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to accept assignment',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRejectAssignment = async () => {
    try {
      setLoading(true);
      
      const assignmentToken = order.assignmentToken;
      
      if (!assignmentToken) {
        setSnackbar({
          open: true,
          message: 'Assignment token not found.',
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      const response = await orderAssignmentAPI.rejectAssignment(
        assignmentToken,
        'Not available'
      );
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Assignment rejected',
          severity: 'info'
        });
        
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (error) {
      console.error('Reject assignment error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to reject assignment',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={onBack}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Sample Collection Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Order #{order.orderId}
          </Typography>
        </Box>
      </Stack>

      {/* Assignment Status Alert */}
      {isPending && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            New Sample Collection Assignment
          </Typography>
          <Typography variant="body2">
            Please review the details and accept to proceed with sample collection
          </Typography>
        </Alert>
      )}

      {isAccepted && !isSampleCollected && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Assignment accepted! Ready for sample collection
        </Alert>
      )}

      {/* Order Status Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              Order Status
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={order.assignmentStatus || 'N/A'}
                color={
                  order.assignmentStatus === 'accepted' 
                    ? 'success' 
                    : order.assignmentStatus === 'pending' 
                    ? 'warning' 
                    : 'default'
                }
                size="small"
              />
              {order.isPaid !== undefined && (
                <Chip
                  label={order.isPaid ? 'PAID' : 'PAYMENT DUE'}
                  color={order.isPaid ? 'success' : 'error'}
                  size="small"
                />
              )}
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Order Type
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                Sample Collection
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary">
                ₹{order.totalAmount}
              </Typography>
            </Grid>
            {order.paymentMethod && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Payment Method
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {order.paymentMethod.toUpperCase()}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Resident Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Person color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Patient Details
            </Typography>
          </Stack>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {order.customerName}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Phone Number
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Phone fontSize="small" color="action" />
                <a 
                  href={`tel:${order.customerPhone}`} 
                  style={{ 
                    color: '#007bff', 
                    textDecoration: 'none',
                    fontWeight: 500 
                  }}
                >
                  {order.customerPhone}
                </a>
              </Stack>
            </Grid>

            {order.patientAge && (
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Age
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {order.patientAge} years
                </Typography>
              </Grid>
            )}

            {order.patientGender && (
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Gender
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {order.patientGender}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Test Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Biotech color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Test Details
            </Typography>
          </Stack>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Test/Service Name
              </Typography>
              <Typography variant="body1" fontWeight={600} color="primary">
                {order.serviceName}
              </Typography>
            </Grid>

            {order.testType && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Test Type
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {order.testType}
                </Typography>
              </Grid>
            )}

            {order.sampleType && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Required Sample Type
                </Typography>
                <Chip label={order.sampleType} size="small" color="info" />
              </Grid>
            )}

            {order.specialInstructions && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<Warning />}>
                  <Typography variant="body2" fontWeight={600}>
                    Special Instructions
                  </Typography>
                  <Typography variant="body2">
                    {order.specialInstructions}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Lab Details */}
      {order.labDetails && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Business color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Lab Details
              </Typography>
            </Stack>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Lab Name
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {order.labDetails.labName || 'Central Lab'}
                </Typography>
              </Grid>

              {order.labDetails.labAddress && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Lab Address
                  </Typography>
                  <Typography variant="body2">
                    {order.labDetails.labAddress}
                  </Typography>
                </Grid>
              )}

              {order.labDetails.labPhone && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Lab Contact
                  </Typography>
                  <Typography variant="body2">
                    {order.labDetails.labPhone}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Collection Address */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <LocationOn color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Collection Address
            </Typography>
          </Stack>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            {order.customerAddress?.fullAddress || 'Address not available'}
          </Typography>
          
          {order.customerAddress?.landmark && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Landmark:</strong> {order.customerAddress.landmark}
            </Typography>
          )}
          
          {order.customerAddress?.fullAddress && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<LocationOn />}
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                order.customerAddress.fullAddress
              )}`}
              target="_blank"
            >
              Open in Maps
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Stack spacing={2}>
        {/* Accept/Reject Assignment */}
        {isPending && (
          <>
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              onClick={handleAcceptAssignment}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              sx={{ py: 1.5 }}
            >
              {loading ? 'Accepting...' : 'Accept Assignment'}
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              size="large"
              fullWidth
              onClick={handleRejectAssignment}
              disabled={loading}
              startIcon={<Cancel />}
              sx={{ py: 1.5 }}
            >
              Reject Assignment
            </Button>
          </>
        )}

        {/* Start Sample Collection */}
        {isAccepted && !isSampleCollected && (
          <>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={() => onOrderUpdate('sampleCollection')}
              startIcon={<Biotech />}
              sx={{ py: 1.5 }}
            >
              Start Sample Collection
            </Button>

            <Button
              variant="outlined"
              color="warning"
              size="large"
              fullWidth
              onClick={onReportFailure}
              startIcon={<Warning />}
            >
              Report Collection Failure
            </Button>
          </>
        )}

        {/* Already Collected */}
        {isSampleCollected && !isCompleted && (
          <Alert severity="info">
            Samples collected. Please complete payment verification and lab transfer.
          </Alert>
        )}

        {/* Completed */}
        {isCompleted && (
          <Alert severity="success">
            Sample collection completed successfully!
          </Alert>
        )}

        {/* Back Button */}
        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={onBack}
          disabled={loading}
        >
          Back to Orders
        </Button>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PhlebotomistOrderDetails;